import LogicExpression from "@bloglovin/inspire-engine/Helpers/LogicExpression.js";
import {
  collectLabelProblems,
  doesLabelReadFields,
  toLabelParts,
} from "%/dynamic-component-editor/classes/feed-separator-label";
import {
  BUCKET_OPERATORS,
  JOIN_AND,
  JOIN_OR,
  OPERATORS,
  SPLIT_OPERATORS,
  buildBucketMembership,
  buildComparison as buildConditionComparison,
  buildDayBucket,
  buildMonthBucket,
  buildParamSource,
  findOperatorDefinition,
  normalizeFieldType,
  operatorsForFieldType,
} from "%/dynamic-component-editor/classes/condition-rule";

// Authored shape of one feed separator rule, and its translation into the
// engine's shipped betweenComponentConfig
// (app.schema.json $defs.betweenComponentConfig):
//
//   { template, context, visibilityExpression, leading }
//
// The label rides as a `constant` context entry rather than needing a new
// engine primitive: injectBetweenComponents resolves config.context through
// resolveContextMap, which runs every entry through resolveOneParam, and that
// already understands { source: "constant", value }.

const KEY_SEPARATOR_LABEL_CONTEXT = "separatorLabel";

// Re-exported so the inspector toolset and the modal keep importing the
// condition vocabulary from one place.
export {
  OPERATORS,
  normalizeFieldType,
  operatorsForFieldType,
};

// Tautology used as the leading-gap expression for boundary-only rules.
const ALWAYS = { source: "constant", value: true };

// A SPLIT condition asks "did this change across the gap?", which only a host
// holding a PAIR of rows can answer. condition-rule.js refuses these for exactly
// that reason and delegates here.
function buildSplitComparison(condition) {
  const { fieldName, operator } = condition;
  if (operator === "dayChanged") {
    return { op: "ne", left: buildDayBucket("before", fieldName), right: buildDayBucket("after", fieldName) };
  }
  if (operator === "monthChanged") {
    return { op: "ne", left: buildMonthBucket("before", fieldName), right: buildMonthBucket("after", fieldName) };
  }
  return {
    op: "ne",
    left: buildParamSource("before", fieldName),
    right: buildParamSource("after", fieldName),
  };
}

function buildComparison(condition) {
  return buildConditionComparison(condition, "after", buildSplitComparison);
}

// Membership test for one rule, in the scope of `paramName` ("before"/"after"),
// EXCLUSIVE of every bucket declared above it. Sections are ordinal: "this week"
// authored under "today" means "within 7 days AND not today". Without this the
// buckets overlap (today IS within 7 days) and the later section never fires.
function buildRuleMembership(rule, earlierRules, paramName) {
  const conditions = (rule?.conditions || []).filter(condition => condition && condition.fieldName && condition.operator);
  const own = conditions
      .filter(condition => BUCKET_OPERATORS.has(condition.operator))
      .map(condition => buildBucketMembership(paramName, condition.fieldName, condition.operator, condition.value));
  if (own.length === 0) {
    return null;
  }
  const mine = own.length === 1 ? own[0] : { op: JOIN_AND, operands: own };

  const exclusions = earlierRules
      .map(earlier => buildRuleMembership(earlier, [], paramName))
      .filter(Boolean)
      .map(expr => ({ op: "not", operand: expr }));

  if (exclusions.length === 0) {
    return mine;
  }
  return { op: JOIN_AND, operands: [mine, ...exclusions] };
}

// `earlierRules` are the sections declared ABOVE this one, used to make bucket
// membership exclusive and ordinal.
function buildVisibilityExpression(rule, earlierRules = []) {
  const conditions = (rule?.conditions || []).filter(condition => condition && condition.fieldName && condition.operator);
  if (conditions.length === 0) {
    return null;
  }

  const hasBucket = conditions.some(condition => BUCKET_OPERATORS.has(condition.operator));
  const nonBucket = conditions
      .filter(condition => !BUCKET_OPERATORS.has(condition.operator))
      .map(buildComparison);

  if (!hasBucket) {
    return nonBucket.length === 1
      ? nonBucket[0]
      : { op: rule.join === JOIN_OR ? JOIN_OR : JOIN_AND, operands: nonBucket };
  }

  // A bucket section fires on the row that ENTERS the bucket: the row after the
  // gap is in it and the row before the gap is not. `leading` covers the very
  // first row, where there is no "before".
  const afterIn = buildRuleMembership(rule, earlierRules, "after");
  const beforeIn = buildRuleMembership(rule, earlierRules, "before");
  const transition = { op: JOIN_AND, operands: [afterIn, { op: "not", operand: beforeIn }] };

  if (nonBucket.length === 0) {
    return transition;
  }
  // Extra non-bucket conditions narrow the section further.
  return { op: rule.join === JOIN_OR ? JOIN_OR : JOIN_AND, operands: [transition, ...nonBucket] };
}

// The expression used on the FIRST gap, where there is no row before. A
// transition test is meaningless there, so this is membership ONLY: still
// exclusive of the buckets above it, so the correct section wins for whatever
// the feed actually opens with. Returns null when the rule is not bucket-based.
function buildLeadingExpression(rule, earlierRules = []) {
  const conditions = (rule?.conditions || []).filter(condition => condition && condition.fieldName && condition.operator);
  if (conditions.length === 0) {
    return null;
  }
  if (!conditions.some(condition => BUCKET_OPERATORS.has(condition.operator))) {
    // A boundary rule ("the day changes") asks whether something differs across
    // a gap. On the first gap there is nothing before it, so the first row
    // always opens a new group: match unconditionally rather than comparing
    // against a row that does not exist.
    return { op: "eq", left: ALWAYS, right: ALWAYS };
  }
  return buildRuleMembership(rule, earlierRules, "after");
}

// The condition that makes this a split rule, if any.
function findSplitCondition(rule) {
  const conditions = (rule?.conditions || []).filter(condition => condition && condition.fieldName && condition.operator);
  return conditions.find(condition => SPLIT_OPERATORS.has(condition.operator)) ?? null;
}

// One authored rule -> one betweenComponentConfig. Returns null for a rule with
// no usable conditions, so a half-authored row never emits a config that would
// match every gap.
function buildBetweenComponentConfig(rule, componentId, earlierRules = []) {
  const visibilityExpression = buildVisibilityExpression(rule, earlierRules);
  if (!visibilityExpression) {
    return null;
  }
  const config = {
    template: componentId,
    context: {
      [KEY_SEPARATOR_LABEL_CONTEXT]: { source: "constant", value: String(rule.label ?? "") },
    },
    visibilityExpression,
  };
  // A label carrying field tokens ("{occurrenceStart|ddd, MMM D}") resolves per
  // separator off the boundary row, so one SPLIT rule can name every separator it
  // produces. A plain-text label emits a single constant, which is exactly what
  // the label used to be.
  const labelParts = toLabelParts(rule.label ?? "");
  if (labelParts.length > 0) {
    config.labelParts = labelParts;
  }
  if (rule.leading) {
    config.leading = true;
  }
  const leadingExpression = buildLeadingExpression(rule, earlierRules);
  if (leadingExpression) {
    // Evaluated only on the first gap (before === null); see
    // buildLeadingExpression. Kept separate so the normal per-gap expression
    // stays a pure transition test.
    config.leadingExpression = leadingExpression;
  }
  return config;
}

export function buildBetweenComponentConfigs(rules, componentId) {
  const list = rules || [];
  return list
      .map((rule, index) => buildBetweenComponentConfig(rule, componentId, list.slice(0, index)))
      .filter(Boolean);
}

// Advisory notes: not errors, but things that will surprise an author.
//
// A separator appears on every row that ENTERS its group. If the feed is not
// ordered by the thing the rule matches on, the group is entered more than
// once and the separator repeats. Date rules are safe because the feed is
// date-ordered; a category rule ("type is livestream") is not, unless the feed
// happens to be sorted by that field.
export function collectRuleNotes(rule, sortedByField = "") {
  const conditions = (rule?.conditions || []).filter(condition => condition && condition.fieldName && condition.operator);
  if (conditions.length === 0) {
    return [];
  }
  const notes = [];
  // A split rule makes one separator per change in the data, so a fixed label
  // reads identically on all of them -- almost never what the author wanted.
  if (findSplitCondition(rule) && !doesLabelReadFields(rule.label)) {
    notes.push(
      "Every separator from this rule will read the same. Add a field to the separator "
      + "so each one names its own group.",
    );
  }
  const repeatable = conditions.filter(condition => {
    if (BUCKET_OPERATORS.has(condition.operator)) {
      return false;
    }
    if (condition.operator === "changed" || condition.operator === "dayChanged"
      || condition.operator === "monthChanged") {
      return false;
    }
    return condition.fieldName !== sortedByField;
  });
  if (repeatable.length > 0) {
    const names = [...new Set(repeatable.map(condition => condition.fieldName))].join(", ");
    notes.push(
      `This separator can appear more than once, because the feed is not ordered by ${names}.`,
    );
  }
  return notes;
}

// Author-facing validation. Returns [] when the rule is publishable.
export function collectRuleProblems(rule, fieldCandidates = []) {
  const problems = [];
  if (!rule || typeof rule !== "object") {
    return ["rule is not an object"];
  }
  if (!String(rule.label ?? "").trim()) {
    problems.push("Give this separator a name");
  }
  problems.push(...collectLabelProblems(rule.label, fieldCandidates));
  const conditions = rule.conditions || [];
  if (conditions.length === 0) {
    problems.push("Add at least one condition");
  }
  conditions.forEach((condition, index) => {
    const position = `Condition ${index + 1}`;
    if (!condition?.fieldName) {
      problems.push(`${position}: choose a field`);
      return;
    }
    const def = findOperatorDefinition(condition.fieldType, condition.operator);
    if (!def) {
      problems.push(
        `${position}: that condition does not work with this field`,
      );
      return;
    }
    if (def.takesValue && String(condition.value ?? "").trim() === "") {
      problems.push(`${position}: "${def.name}" needs a value`);
    }
    if (def.takesValue && def.valueType === "number" && !Number.isFinite(Number(condition.value))) {
      problems.push(`${position}: "${def.name}" needs a number`);
    }
  });

  // Last line of defence: the emitted AST must satisfy the engine's own
  // validator, so a shape the builder allows can never fail silently at render.
  const expression = buildVisibilityExpression(rule);
  if (expression) {
    try {
      LogicExpression.assertValid(expression);
    } catch (error) {
      // The engine's own reason, not a generic refusal: without it the author
      // is told to change something with no way to tell what.
      problems.push(`This combination cannot be saved: ${error.message}`);
    }
  }
  return problems;
}

